// UsersPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import ContentWMax from '../../Components/PageLayout/ContentWMax';
import PageTitle from '../../Components/PageTitle/PageTitle';
import { Spin } from 'antd';
import Select from 'react-select';
import styles from './UserManagement.module.scss';
import UserFormModal from './UserFormModal';
import api from '../../services/api';

const USERS_API_BASE = process.env.REACT_APP_SUPPORT_API_URL || '';

const roleOptions = [
    { value: 'ROLE_SUPER_ADMIN', label: 'Супер Администратор' },
    { value: 'ROLE_ADMIN', label: 'Администратор' },
    { value: 'ROLE_MANAGER', label: 'Менеджер' },
    { value: 'ROLE_OBSERVER', label: 'Наблюдатель' },
    { value: 'ROLE_YK', label: 'Сотрудник УК' },
    { value: 'ROLE_SPECIALIST', label: 'Специалист' },
    { value: 'ROLE_SPECIALIST_TS', label: 'Специалист технической поддержки' },
    { value: 'ROLE_OKK', label: 'ОКК' },
    { value: 'ROLE_PARTNER', label: 'Партнер' },
    { value: 'ROLE_MASTER', label: 'Мастер' },
    { value: 'ROLE_EXTERNAL', label: 'Внешний пользователь' },
];

export default function UsersPage() {
    const [isLoad] = useState(true);
    const [users, setUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [roleFilter, setRoleFilter] = useState([]); // multi

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get(`${USERS_API_BASE}/users`);
            setUsers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Ошибка при загрузке пользователей:', error);
        }
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingUser(null);
    };

    const handleSaveUser = async () => {
        await fetchUsers();
        handleCloseModal();
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) return;
        try {
            await api.delete(`${USERS_API_BASE}/user/${userId}`);
            await fetchUsers();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Ошибка при удалении пользователя:', error);
        }
    };

    const filteredUsers = useMemo(() => {
        const selectedRoles = (roleFilter || []).map((r) => r.value);
        if (!selectedRoles.length) return users;

        return users.filter((u) => {
            const roles = Array.isArray(u.roles) ? u.roles : [];
            return selectedRoles.some((r) => roles.includes(r));
        });
    }, [users, roleFilter]);

    const formatRoles = (roles) => {
        if (!Array.isArray(roles)) return '';
        return roles
            .map((r) => roleOptions.find((x) => x.value === r)?.label || r)
            .join(', ');
    };

    return (
        <ContentWMax>
            <PageTitle title="Пользователи" />

            {isLoad ? (
                <div className={styles.userManagement}>
                    <div className={styles.headerRow}>
                        <h1>Управление пользователями</h1>

                        <button onClick={handleAddUser} className={styles.addButton}>
                            Добавить пользователя
                        </button>
                    </div>

                    <div className={styles.filters}>
                        <div className={styles.filterItem}>
                            <div className={styles.filterLabel}>Фильтр по ролям</div>
                            <Select
                                options={roleOptions}
                                value={roleFilter}
                                onChange={(v) => setRoleFilter(v || [])}
                                isMulti
                                isSearchable
                                placeholder="Выберите роли"
                            />
                        </div>
                    </div>

                    {/* Desktop table */}
                    <div className={styles.tableWrap}>
                        <table className={styles.userTable}>
                            <thead>
                            <tr>
                                <th>Имя</th>
                                <th>Телефон</th>
                                <th>Роль(и)</th>
                                <th>Действия</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredUsers.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.name}</td>
                                    <td>{user.phone || '-'}</td>
                                    <td>{formatRoles(user.roles)}</td>
                                    <td className={styles.actions}>
                                        <button onClick={() => handleEditUser(user)} aria-label="edit">
                                            ✏️
                                        </button>
                                        <button onClick={() => handleDeleteUser(user.id)} aria-label="delete">
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className={styles.userCards}>
                        {filteredUsers.map((user) => (
                            <div key={user.id} className={styles.userCard}>
                                <div className={styles.cardRow}>
                                    <div className={styles.cardLabel}>Имя</div>
                                    <div className={styles.cardValue}>{user.name}</div>
                                </div>
                                <div className={styles.cardRow}>
                                    <div className={styles.cardLabel}>Телефон</div>
                                    <div className={styles.cardValue}>{user.phone || '-'}</div>
                                </div>
                                <div className={styles.cardRow}>
                                    <div className={styles.cardLabel}>Роль(и)</div>
                                    <div className={styles.cardValue}>{formatRoles(user.roles)}</div>
                                </div>

                                <div className={styles.cardActions}>
                                    <button onClick={() => handleEditUser(user)}>Редактировать</button>
                                    <button onClick={() => handleDeleteUser(user.id)}>Удалить</button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {isModalOpen && (
                        <UserFormModal user={editingUser} onSave={handleSaveUser} onClose={handleCloseModal} />
                    )}
                </div>
            ) : (
                <>
                    <Spin size="small" /> <span className="ml-s">Загрузка данных...</span>
                </>
            )}
        </ContentWMax>
    );
}
